import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../constants";
import { useAuth } from "./AuthContext";

export const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const { token } = useAuth();

  const fetchCart = async () => {
    if (!token) {
      setCart({ items: [], total: 0 });
      return;
    }
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart');
      setCart({ items: [], total: 0 });
    }
  };

  useEffect(() => {
    if (token) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
    }
  }, [token]);

  const addToCart = async (productId, quantity = 1) => {
    if (!token) {
      toast.error('Please login to add items to cart');
      return false;
    }
    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
      toast.success('Added to cart');
      return true;
    } catch (error) {
      toast.error('Failed to add to cart');
      return false;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      await axios.put(`${API}/cart/update`, { product_id: productId, quantity }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
    } catch (error) {
      toast.error('Failed to update cart');
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart({ items: [], total: 0 });
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};
